import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-grief",
  title: "A prayer for grief — for the weeks after everyone stops asking",
  h1: "A prayer for grief",
  description:
    "A prayer for the long middle of grief, four passages that do not hurry you, and why the Bible's shortest verse is the most useful one here.",
  targets: [
    "prayer for grief",
    "prayer after losing someone",
    "prayer for a grieving heart",
    "prayer for comfort in loss",
  ],
  published: "2026-09-03",
  standfirst:
    "Grief is not a problem to be solved, and this page is not going to try. It is a prayer for staying upright in the middle of it.",
  takeaways: [
    "You are not required to be further along than you are. Nothing in these passages sets a timetable, and the people who do are guessing.",
    "John 11:35 has Jesus weeping at a graveside minutes before raising the man — knowing the outcome did not remove the grief, and it is not supposed to remove yours.",
    "The hardest stretch is usually months two to six, when the attention has gone and the loss has not.",
  ],
  when: [
    "In week six, when the messages have stopped and it has got worse rather than better.",
    "On an ordinary morning ambushed by something small — a jar, a song, a habit of reaching for the phone to tell them.",
    "On the birthday, the anniversary, the first Christmas.",
    "When somebody has said something well-meant that landed badly and you smiled anyway.",
  ],
  prayer: [
    "God, they are gone, and I keep finding new places where they used to be.",
    "I am not asking to feel better yet. I do not think I am supposed to and I am tired of people implying I should be.",
    "Hold the part of this I cannot look at directly. I will get to it. Not tonight.",
    "Where I am angry — with them, with the doctors, with you — let me say it out loud rather than carry it politely.",
    "Get me through today. And send somebody who will still be asking in March.",
  ],
  short: "God, get me through today. Hold the part I cannot look at yet.",
  honest: [
    "This prayer does not shorten grief and does not claim there is a right length. Anyone offering a timetable is describing their own comfort rather than your loss.",
    "It also does not explain the death. The passages here comfort without explaining, and the book most associated with this subject spends thirty-eight chapters refusing to give a reason.",
    "And it is not a replacement for people or for help. Grief that has become months of not being able to function is worth taking to a doctor or a counsellor, and doing so is not a failure of faith.",
  ],
  verses: [
    {
      verse: {
        text: "Jesus wept.",
        ref: "John 11:35",
      },
      note: "Two words, at the grave of a friend, minutes before raising him. He knew what was about to happen and he wept anyway, which disposes of the idea that grief is a failure of faith more efficiently than any argument. The verse before says he was deeply moved — the Greek is closer to anger than to sadness.",
    },
    {
      verse: {
        text: "Yahweh is near to those who have a broken heart, and saves those who have a crushed spirit.",
        ref: "Psalm 34:18",
      },
      note: "Near, which is a claim about location rather than about repair. The psalm does not say the broken heart is mended, and it does not say when. It says where God is standing, and on the days when nothing else in the sentence is available, the location is the part that holds.",
    },
    {
      verse: {
        text: "Blessed are those who mourn, for they shall be comforted.",
        ref: "Matthew 5:4",
      },
      note: "The tense is worth sitting with: they shall be, not they are. The comfort is promised and not yet delivered, and the blessing lands on the person in the middle. Nothing here asks anybody to hurry, and the sentence structure itself concedes that the mourning comes first and lasts.",
    },
    {
      verse: {
        text: "He will wipe away every tear from their eyes. Death will be no more; neither will there be mourning, nor crying, nor pain any more. The first things have passed away.",
        ref: "Revelation 21:4",
      },
      note: "Read at almost every funeral, and the detail that survives repetition is the gesture: somebody wiping tears off a face, which is a small and physical thing to promise at the end of a book full of cosmic ones. Note also that the tears are still there to be wiped — this is not a promise that you should not have cried.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Month three is worse than week one",
      id: "month-three-is-worse-than-week-one",
    },
    {
      type: "p",
      text: "The first fortnight has casseroles, messages and people in the house. Then everyone goes back to their own lives, correctly, and the loss is exactly the same size as it was.",
    },
    {
      type: "p",
      text: "So the request in the prayer for somebody who will still be asking in March is not sentimental. It is the practical gap, and it is worth naming to God and to one specific friend.",
    },
    {
      type: "h2",
      text: "You are allowed to be angry",
      id: "you-are-allowed-to-be-angry",
    },
    {
      type: "p",
      text: "A third of the psalms are complaints, several of them accusatory, and they were kept. Job argues with God for thirty-five chapters and is told at the end that he spoke rightly, while his friends — who defended God — are not.",
    },
    {
      type: "note",
      text: "Polite grief is the kind that goes underground. The tradition has more room for anger addressed to God than most of its adherents have.",
    },
    {
      type: "h2",
      text: "The phone in the small hours",
      id: "the-phone-in-the-small-hours",
    },
    {
      type: "p",
      text: "Grief and insomnia arrive together, and the thing within reach at three in the morning is {a screen}. It supplies a few hours of not thinking, and it takes them from the sleep that would otherwise have made tomorrow survivable.",
      links: {
        "a screen": "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "p",
      text: "There is no discipline being asked for here. Just the observation that it does not help, from a source that is not judging you for it.",
    },
  ],
  faq: [
    {
      q: "What can I pray when I am grieving?",
      a: 'Ask for the day rather than the healing: "God, get me through today — hold the part I cannot look at yet." Nothing in Scripture sets a timetable for this.',
    },
    {
      q: "Which Bible verse helps with grief?",
      a: "John 11:35 — Jesus wept, at a graveside, minutes before raising the man — and Psalm 34:18, which claims nearness rather than repair. Both are useful precisely because neither hurries you.",
    },
    {
      q: "Is it wrong to be angry with God after a death?",
      a: "No. A third of the psalms are complaints, some of them accusatory, and they were kept in the book. Job argues for thirty-five chapters and is told he spoke rightly, while the friends defending God are not.",
    },
  ],
  relatedPrayers: ["prayer-for-hope", "prayer-when-you-cant-pray"],
  related: ["how-to-stop-scrolling-at-night"],
  relatedTopics: ["bible-verses-about-grief", "bible-verses-about-peace"],
  relatedPassages: ["psalm-23", "isaiah-41-10"],
};
